import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { logout, selectUser } from '../store/slices/authSlice';
import { ThemeToggle } from '../components/ThemeToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import apiClient from '../services/api';
import { PlatformAnalytics, SubscriptionAnalytics, UsageAnalytics } from '../types/api';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [platformAnalytics, setPlatformAnalytics] = useState<PlatformAnalytics | null>(null);
  const [subscriptionAnalytics, setSubscriptionAnalytics] = useState<SubscriptionAnalytics | null>(null);
  const [usageAnalytics, setUsageAnalytics] = useState<UsageAnalytics | null>(null);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const isAdmin = await apiClient.verifyAdminAccess();
        if (!isAdmin) {
          navigate('/dashboard');
          return;
        }
        await loadAnalytics();
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/dashboard');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [platform, subscription, usage] = await Promise.all([
        apiClient.getPlatformAnalytics(),
        apiClient.getSubscriptionAnalytics(),
        apiClient.getUsageAnalytics()
      ]);

      setPlatformAnalytics(platform);
      setSubscriptionAnalytics(subscription);
      setUsageAnalytics(usage);
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <LoadingSpinner size="lg" text="Loading admin dashboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-content">
          <div>
            <h1>Admin Dashboard</h1>
            {user && <p>Welcome, {user.full_name || user.username}</p>}
          </div>
          <div className="header-actions">
            <ThemeToggle />
            <Link to="/dashboard" className="btn btn-outline">
              Back to Dashboard
            </Link>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        {error && (
          <div className="alert alert-error">
            {error}
            <button
              className="alert-retry"
              onClick={loadAnalytics}
            >
              Retry
            </button>
          </div>
        )}

        {/* Quick Actions */}
        <section className="admin-section">
          <h2>Quick Actions</h2>
          <div className="admin-actions-grid">
            <Link to="/admin/users" className="admin-action-card">
              <div className="action-icon">👥</div>
              <h3>User Management</h3>
              <p>Manage users, roles, and permissions</p>
            </Link>
            <Link to="/admin/plans" className="admin-action-card">
              <div className="action-icon">💎</div>
              <h3>Subscription Plans</h3>
              <p>Manage pricing and plan features</p>
            </Link>
            <Link to="/admin/analytics" className="admin-action-card">
              <div className="action-icon">📊</div>
              <h3>Analytics</h3>
              <p>Detailed analytics and reports</p>
            </Link>
            <Link to="/admin/ai" className="admin-action-card">
              <div className="action-icon">🤖</div>
              <h3>AI Management</h3>
              <p>Configure AI models, prompts, and usage</p>
            </Link>
            <Link to="/admin/settings" className="admin-action-card">
              <div className="action-icon">⚙️</div>
              <h3>Settings</h3>
              <p>Platform configuration and admin tools</p>
            </Link>
          </div>
        </section>

        {/* Platform Overview */}
        {platformAnalytics && (
          <section className="admin-section">
            <h2>Platform Overview</h2>
            <div className="analytics-grid">
              <div className="analytics-card">
                <h3>Users</h3>
                <div className="analytics-content">
                  <div className="metric">
                    <span className="metric-value">{platformAnalytics.users.total.toLocaleString()}</span>
                    <span className="metric-label">Total Users</span>
                  </div>
                  <div className="metrics-row">
                    <div className="sub-metric">
                      <span className="sub-metric-value">{platformAnalytics.users.active.toLocaleString()}</span>
                      <span className="sub-metric-label">Active</span>
                    </div>
                    <div className="sub-metric">
                      <span className="sub-metric-value">{platformAnalytics.users.new_this_month.toLocaleString()}</span>
                      <span className="sub-metric-label">New This Month</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="analytics-card">
                <h3>Content</h3>
                <div className="analytics-content">
                  <div className="metric">
                    <span className="metric-value">{platformAnalytics.content.total_memoirs.toLocaleString()}</span>
                    <span className="metric-label">Total Memoirs</span>
                  </div>
                  <div className="metrics-row">
                    <div className="sub-metric">
                      <span className="sub-metric-value">{platformAnalytics.content.total_chapters.toLocaleString()}</span>
                      <span className="sub-metric-label">Chapters</span>
                    </div>
                    <div className="sub-metric">
                      <span className="sub-metric-value">{(platformAnalytics.content.total_words / 1000000).toFixed(1)}M</span>
                      <span className="sub-metric-label">Words</span>
                    </div>
                  </div>
                </div>
              </div>

              {subscriptionAnalytics && (
                <div className="analytics-card">
                  <h3>Revenue</h3>
                  <div className="analytics-content">
                    <div className="metric">
                      <span className="metric-value">{formatCurrency(subscriptionAnalytics.revenue.estimated_monthly)}</span>
                      <span className="metric-label">Monthly Revenue</span>
                    </div>
                    <div className="metrics-row">
                      <div className="sub-metric">
                        <span className="sub-metric-value">{subscriptionAnalytics.metrics.paid_users.toLocaleString()}</span>
                        <span className="sub-metric-label">Paid Users</span>
                      </div>
                      <div className="sub-metric">
                        <span className="sub-metric-value">{(subscriptionAnalytics.metrics.conversion_rate * 100).toFixed(1)}%</span>
                        <span className="sub-metric-label">Conversion</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="analytics-card">
                <h3>Subscriptions</h3>
                <div className="analytics-content">
                  {subscriptionAnalytics && Object.entries(subscriptionAnalytics.distribution).map(([tier, count]) => (
                    <div key={tier} className="subscription-metric">
                      <div className="subscription-tier">
                        <span className="tier-name">{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
                        <span className="tier-count">{count.toLocaleString()}</span>
                      </div>
                      <div className="tier-progress">
                        <div
                          className="tier-progress-bar"
                          style={{
                            width: `${(count / platformAnalytics.users.total) * 100}%`,
                            backgroundColor: tier === 'free' ? '#6b7280' : tier === 'pro' ? '#3b82f6' : '#10b981'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Recent Activity */}
        <section className="admin-section">
          <div className="section-header">
            <h2>Recent Activity</h2>
            <Link to="/admin/actions" className="btn btn-outline btn-sm">
              View All
            </Link>
          </div>
          <div className="activity-placeholder">
            <p>Activity feed will be implemented with admin actions history</p>
          </div>
        </section>

        {/* System Status */}
        <section className="admin-section">
          <h2>System Status</h2>
          <div className="status-grid">
            <div className="status-card">
              <div className="status-indicator status-good"></div>
              <span>API Services</span>
              <span className="status-text">Operational</span>
            </div>
            <div className="status-card">
              <div className="status-indicator status-good"></div>
              <span>Database</span>
              <span className="status-text">Operational</span>
            </div>
            <div className="status-card">
              <div className="status-indicator status-good"></div>
              <span>AI Services</span>
              <span className="status-text">Operational</span>
            </div>
            <div className="status-card">
              <div className="status-indicator status-warning"></div>
              <span>Background Jobs</span>
              <span className="status-text">Monitoring</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;
